/**
 * 스플래시 로고 마크 — 유리 질감 재해석 (원본 docs/design/glass-loading.html).
 *
 * 시퀀스(1.05초): 아래 원 등장 → 위 원 등장 → 물방울 낙하 → 타공이 열리며 파문.
 * 모션 클래스·keyframes 는 styles/components.css 의 `.app-splash-mark` 블록.
 *
 * 성능을 위해 레이어를 나눈다 — 움직이는 것은 전부 HTML 요소의 opacity/transform 이라 GPU 합성으로 돈다.
 *   .glass-lower / .glass-upper : 블러 필터가 든 정적 SVG. 등장 페이드만 wrapper opacity 로.
 *   .hole   : 배경색 원(div). SVG mask 대신 위에 덮어 타공을 표현 — 배경이 단색(--c-bg)이라 결과가 같다.
 *   .drop   : 물방울 SVG(필터 없음)를 wrapper transform 으로 낙하.
 *   .ripple : 파문 원(div) scale+opacity.
 *   (원본처럼 mask 안의 원을 움직이면 매 프레임 필터 그룹을 다시 래스터라이즈해서 버벅인다)
 *
 * 좌표: viewBox(-3 -5 34.42 36.29) 기준 타공 중심 (8.09, 20.19) → left 32.22% / top 69.41%, 지름 7.36 → 폭의 21.38%.
 *
 * - `play`  : 한 번 재생(마운트 직후 true 로 두면 즉시 시작)
 * - `loop`  : 첫 재생이 끝난 뒤 로딩이 계속될 때 물방울·파문만 반복
 * - id 는 페이지 뒤에서 그려지는 다른 SVG 와 충돌하지 않도록 `sm-` 접두어
 */
const VIEWBOX = "-3 -5 34.42 36.29";

export function SplashMark({
  play = true,
  loop = false,
  onSequenceEnd,
}: {
  play?: boolean;
  loop?: boolean;
  onSequenceEnd?: () => void;
}) {
  const cls = ["app-splash-mark", play ? "is-play" : "", loop ? "is-loop" : ""].filter(Boolean).join(" ");
  return (
    <div
      className={cls}
      role="img"
      aria-hidden="true"
      onAnimationEnd={(e) => {
        // 여러 애니메이션이 버블링되므로 시퀀스의 마지막(타공)만 골라 받는다
        if (e.animationName === "app-splash-hole") onSequenceEnd?.();
      }}
    >
      {/* 아래 원 (분홍) */}
      <svg className="glass-lower" xmlns="http://www.w3.org/2000/svg" viewBox={VIEWBOX}>
        <defs>
          <linearGradient id="sm-bottom" x1="0" y1="0" x2=".8" y2="1">
            <stop stopColor="#f5dce6" />
            <stop offset=".55" stopColor="#f0b5cd" />
            <stop offset="1" stopColor="#d4a7be" />
          </linearGradient>
        </defs>
        <circle cx="8.095" cy="20.195" r="8.095" fill="url(#sm-bottom)" />
      </svg>

      {/* 위 원 (반투명 라벤더) + 아래 원에 드리우는 그림자·접촉선·하이라이트 */}
      <svg className="glass-upper" xmlns="http://www.w3.org/2000/svg" viewBox={VIEWBOX}>
        <defs>
          <clipPath id="sm-lower">
            <circle cx="8.095" cy="20.195" r="8.095" />
          </clipPath>
          <clipPath id="sm-upper">
            <circle cx="16.54" cy="11.88" r="11.88" />
          </clipPath>
          <clipPath id="sm-outside-upper">
            <path clipRule="evenodd" d="M-5-5H35V35H-5Z M28.42 11.88A11.88 11.88 0 1 0 4.66 11.88A11.88 11.88 0 1 0 28.42 11.88Z" />
          </clipPath>
          <linearGradient id="sm-top" x1="0" y1="0" x2=".8" y2="1">
            <stop stopColor="#e1e7fa" stopOpacity="0.72" />
            <stop offset=".45" stopColor="#9eace3" stopOpacity="0.72" />
            <stop offset="1" stopColor="#909ed0" stopOpacity="0.72" />
          </linearGradient>
          <radialGradient id="sm-soft-light" cx=".28" cy=".15" r=".85">
            <stop stopColor="#fff" stopOpacity=".2" />
            <stop offset=".65" stopColor="#fff" stopOpacity="0" />
          </radialGradient>
          <filter id="sm-contact" x="-40%" y="-40%" width="180%" height="180%" colorInterpolationFilters="sRGB">
            <feGaussianBlur stdDeviation=".13" />
          </filter>
          <filter id="sm-cast" x="-40%" y="-40%" width="180%" height="180%" colorInterpolationFilters="sRGB">
            <feGaussianBlur stdDeviation=".62" />
          </filter>
        </defs>
        <g clipPath="url(#sm-lower)">
          <g clipPath="url(#sm-outside-upper)">
            <circle cx="16.26" cy="12.46" r="11.88" fill="#54406f" opacity=".24" filter="url(#sm-cast)" />
            <circle cx="16.48" cy="12.04" r="11.88" fill="#403858" opacity=".25" filter="url(#sm-contact)" />
          </g>
        </g>
        <circle cx="16.54" cy="11.88" r="11.88" fill="url(#sm-top)" />
        <g clipPath="url(#sm-lower)">
          <g clipPath="url(#sm-upper)">
            <path
              d="M4.66 11.88A11.88 11.88 0 0 0 16.54 23.76"
              fill="none"
              stroke="#eef1ff"
              strokeOpacity=".42"
              strokeWidth=".48"
              filter="url(#sm-contact)"
            />
          </g>
        </g>
        <circle cx="16.54" cy="11.88" r="11.88" fill="url(#sm-soft-light)" />
      </svg>

      {/* 타공 — 배경색 원 */}
      <div className="hole" />

      {/* 물방울 */}
      <div className="drop">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox={VIEWBOX}>
          <defs>
            <radialGradient id="sm-water" cx=".28" cy=".23" r=".8">
              <stop stopColor="#fff" />
              <stop offset=".35" stopColor="#e0e7ff" />
              <stop offset="1" stopColor="#8b9dd5" />
            </radialGradient>
          </defs>
          <path
            d="M8.09 17.7C7.8 18.3 6.95 19.1 6.95 19.75A1.14 1.14 0 0 0 9.23 19.75C9.23 19.1 8.38 18.3 8.09 17.7Z"
            fill="url(#sm-water)"
          />
          <ellipse cx="7.76" cy="19.35" rx=".22" ry=".4" fill="white" opacity=".8" />
        </svg>
      </div>

      {/* 파문 */}
      <div className="ripple" />
    </div>
  );
}
